import { scaleLinear, scaleTime } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import { axisBottom, axisLeft } from "d3-axis"
import moment from "moment"
import { find, last, first, get, some, size } from "lodash"
import { createSelector } from "reselect"
import { calculateHighlighter } from "../helpers/highlighter"
import { xAxisTicks } from "../helpers/x-axis-ticks"

const MARGINS = { top: 15, left: 50, right: 8, bottom: 15 }

export const getData = props => props.data
const getDate = props => props.date
const getWidth = props => props.width || 150
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate
const getHeight = props => props.height
const getItems = props => props.items
const getTickFormat = props => props.tickFormat
const getPropsMargins = props => props.margins
const getIsOneDayGraph = props => props.isOneDayGraph

const getMargins = createSelector(
  [getPropsMargins],
  (propsMargins) => {
    return { ...MARGINS, ...propsMargins }
  },
)

const getMinX = createSelector(
  [getStartDate, getData],
  (startDate, data) => {
    if (data && data.length > 0) return first(data).date.toDate()
    return moment(startDate).toDate()
  },
)

const getMaxX = createSelector(
  [getEndDate, getData],
  (endDate, data) => {
    if (data && data.length > 0) return last(data).date.toDate()
    return moment(endDate).toDate()
  },
)

const getXTicks = createSelector(
  [getMinX, getMaxX],
  xAxisTicks,
)

const getMinY = createSelector(
  [getData, getItems],
  (data, items) => data.reduce((min, datum) => {
    return Math.min(min, ...items.filter(({ disabled }) => !disabled).map(({ id }) => datum[id]).filter(datum => Number.isFinite(datum)))
  }, Infinity),
)

const getMaxY = createSelector(
  [getData, getItems],
  (data, items) => data.reduce((max, datum) => {
    return Math.max(max, ...items.filter(({ disabled }) => !disabled).map(({ id }) => datum[id]).filter(datum => Number.isFinite(datum)))
  }, -Infinity),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth, getMargins],
  (minX, maxX, width, margins) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([margins.left, width - margins.right]),
)

export const getScaleY = createSelector(
  [getMinY, getMaxY, getHeight, getMargins],
  (minY, maxY, height, margins) => {
    return scaleLinear()
      .domain([minY, maxY])
      .rangeRound([height - margins.bottom, margins.top])
  },
)

export const getLines = createSelector(
  [getScaleX, getScaleY, getData, getItems],
  (scaleX, scaleY, data, items) => items
    .map(({ id, disabled, color }) => {
      const computeLine = some(data, item => item[id])
      if (computeLine && !disabled) {
        return ({
          id,
          d: line()
            .defined(d => Number.isFinite(get(d, id)))
            .x(d => scaleX(d.date))
            .y(d => scaleY(get(d, id, 0)))
            .curve(curveLinear)(data),
          style: { stroke: color },
        })
      }

      return null
    })
    .filter(item => item),
)

export const getXAxis = createSelector(
  [getHeight, getWidth, getScaleX, getXTicks, getMargins],
  (height, width, scaleX, [ticks, formatter], margins) => ({
    fn: axisBottom()
      .scale(scaleX)
      .ticks(ticks)
      .tickSize(-height + margins.top + margins.bottom)
      .tickFormat(formatter),
    transform: `translate(0,${height - margins.bottom})`,
    baseline: {
      x1: margins.left,
      x2: width - margins.right,
      y1: height - margins.bottom,
      y2: height - margins.bottom,
    },
  }),
)

export const getYAxis = createSelector(
  [getHeight, getWidth, getScaleY, getTickFormat, getMargins],
  (height, width, scaleY, tickFormat, margins) => ({
    fn: axisLeft()
      .scale(scaleY)
      .ticks(6)
      .tickSize(-width + margins.left + margins.right)
      .tickFormat(tickFormat),
    transform: `translate(${margins.left},0)`,
    baseline: {
      x1: margins.left,
      x2: margins.left,
      y1: margins.top,
      y2: height - margins.bottom,
    },
  }),
)

export const getFindByOffset = createSelector(
  [getData, getScaleX],
  (data, scaleX) => offset => {
    const lookup = scaleX.invert(offset - 2) // -2 is to give a margin of selection on the first element
    return find(data, datum => datum.date >= lookup) || last(data)
  },
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getScaleY, getMinX, getMaxX, getMinY, getMaxY, getIsOneDayGraph],
  (date, scaleX, scaleY, minX, maxX, minY, maxY, isOneDayGraph) =>
    isOneDayGraph ? null : calculateHighlighter(date, scaleX, scaleY, minX, maxX, minY, maxY),
)

export const getSingleDotGraph = createSelector(
  [getData],
  (data) => size(data) === 1,
)

export const getGraphData = createSelector(
  [getSingleDotGraph, getData],
  (singleDot, data) => singleDot ? [...data, { ...data[0], date: moment(data[0].date).add(1, "s") }] : data,
)
